/* eslint-disable */

// The action engine is a state machine that executes individual sequential actions one by one. Individual action nodes (or states)
// are represented in a graph with connections between them. Each action has a unique graph ID, calls a specific function, and can exit in 
// one or more ways.

// Special nodes represent the trigger / entry and exit points of the action. Trigger nodes are labelled with specific types that represent
// when they are called by the frontend application. Exit nodes simply represent termination of the action.

// Some actions are used to determine entries in contextual menus based on existance of trigger nodes. In this way, the action engine can also
// be used to determine conditional entries in menu systems.

// Nodes are labelled with input and output socket types, which are just string labels.
// For example, the context menu trigger would have output socket types of "clicked_mote" for the clicked mote, "selected_motes" for the array of
// possibly selected motes, etc.

import { ref, reactive, computed } from 'vue';
// import { inject } from 'vue';
// import { useRouter } from 'vue-router';

// Action types that can be configured. Actions are nodes between trigger and exit nodes that have one or more input sockets, and one or more output sockets.
// Note that all node names must be unique, and they must be represented in key by the uppercase version of the name.
export const ACTION_TYPES = {
	SHOW_DIALOG: 'show_dialog',		// Show a dialog modal with a given dialog type (alert, confirm, prompt) and text
	CONDITIONAL: 'conditional',		// Conditional node, with condition and true/false output sockets
	
	NAVIGATE: 'navigate',
	HTTP_REQUEST: 'http_request',
	UPDATE_DATA: 'update_data',
	CUSTOM_FUNCTION: 'custom_function',
	EMIT_EVENT: 'emit_event',
	SET_VARIABLE: 'set_variable',
};

// Trigger types that can be configured. Triggers are nodes that represent the entry point of an action, with one or more output sockets.
export const TRIGGER_TYPES = {
	CLICK: 'click',
	HOLD: 'hold',
	CONTEXTMENU: 'contextmenu',
	SELECT: 'select',
};

// Node types for the graph system. All node types must be unique among TRIGGER_TYPES and ACTION_TYPES, with a unique EXIT node.
export const NODE_TYPES = {
	...TRIGGER_TYPES,
	EXIT: 'exit',
	...ACTION_TYPES
};

// Sanity check that our node typing is unique.
if (Object.keys(NODE_TYPES).length !== Object.keys(TRIGGER_TYPES).length + Object.keys(ACTION_TYPES).length + 1) {
	throw new Error('Node types are not unique: Action engine cannot initialize!');
}

// The underlying node object consists of the following fields:
// id, type
// The underlying connections object consists of the following fields:
// sourceId, targetId, socketFrom, socketTo

// Global action engine state - we should store all underlying action engine state here. Persists for the app session lifetime, but will be reset on reload.
// Note that we store ALL action graph nodes and connections in these nodes/connections maps - even if they are represented in the UI as separate "actions",
// we simply store them all in one unified "graph". Graph IDs are unique across all actions.
const globalActionState = reactive({
	nodes: new Map(),				// Action nodes in our full graph, mapping from graphId to node object. 
	connections: new Map(),			// Connections between nodes in our full graph, mapping from connectId to connection object. ConnectId is a string of the form 'sourceId-targetId-socketFrom-socketTo'.
	eventHistory: [],				// History of events, array of objects with nodeId, context, result, timestamp.
	debugMode: false,				// Debug mode, boolean.
});

// Core action engine composable.
export function useActionEngine() {
	// Local reactive state.
	const isExecuting = ref(false);
	const lastResult = ref(null);
	const errors = ref([]);

	// Register a new node in the graph for the currently loaded action.
	const registerNode = (id, config) => {
		if (!config.type || !NODE_TYPES[config.type.toUpperCase()]) {
			throw new Error(`Action engine cannot register node ${id}, invalid node type '${config.type}'!`);
		}

		globalActionState.nodes.set(id, {
			id,
			...config,
			createdAt: Date.now()
		});
	};

	// Create a connection between two nodes in the action graph for the currently loaded action.
	const createConnection = (sourceId, targetId, socketFrom, socketTo) => {
		const connectionId = `${sourceId}-${targetId}-${socketFrom}-${socketTo}`;
		if (globalActionState.connections.has(connectionId)) {
			throw new Error(`Connection ${connectionId} already exists!`);
		}
		if (!globalActionState.nodes.get(sourceId) || !globalActionState.nodes.get(targetId)) {
			throw new Error(`Connection ${connectionId} cannot find source or target node!`);
		}

		globalActionState.connections.set(connectionId, {
			id: connectionId,
			sourceId,
			targetId,
			socketFrom,
			socketTo,
			createdAt: Date.now()
		});
	};

	// Execute the action graph for the currently loaded action. Provide a context object to set the initial context for the trigger node only.
	const executeGraph = async (startNodeId, context = {}) => {
		const startNode = globalActionState.nodes.get(startNodeId);
		if (!startNode) {
			throw new Error(`Start node '${startNodeId}' not found`);
		} else if (!TRIGGER_TYPES[startNode.type.toUpperCase()]) {
			throw new Error(`Start node '${startNodeId}' is not a trigger node (type '${startNode.type}')`);
		}

		isExecuting.value = true;
		errors.value = [];

		try {
			const result = await executeNode(startNode, context);
			lastResult.value = result;

			// Add to history for debugging if enabled. We stringify context/results to preserve the data at time of execution.
			if (globalActionState.debugMode) {
				globalActionState.eventHistory.push({
					nodeId: startNodeId,
					context: JSON.parse(JSON.stringify(context)),
					inputs: [],
					results: JSON.parse(JSON.stringify(result)),
					timestamp: Date.now()
				});
			}

			return result;
		} catch (error) {
			errors.value.push(error.message);
			throw error;
		} finally {
			isExecuting.value = false;
		}
	};

	// Execute a single action node in the action graph.
	const executeNode = async (node, context) => {
		const { type, config } = node;

		// Execute the current node
		let result;
		switch (type.toUpperCase()) {
			case NODE_TYPES.INPUT:
				result = context;
				break;
			case NODE_TYPES.OUTPUT:
				result = context;
				break;
			case ACTION_TYPES.NAVIGATE:
				result = await executeNavigateAction(config, context);
				break;
			case ACTION_TYPES.HTTP_REQUEST:
				result = await executeHttpAction(config, context);
				break;
			case ACTION_TYPES.SHOW_DIALOG:
				result = await executeDialogAction(config, context);
				break;
			case ACTION_TYPES.UPDATE_DATA:
				result = await executeUpdateDataAction(config, context);
				break;
			case ACTION_TYPES.SET_VARIABLE:
				result = executeSetVariableAction(config, context);
				break;
			case ACTION_TYPES.EMIT_EVENT:
				result = executeEmitEventAction(config, context);
				break;
			case ACTION_TYPES.CONDITIONAL:
				result = await executeConditionalAction(config, context);
				break;
			case ACTION_TYPES.CUSTOM_FUNCTION:
				result = await executeCustomFunctionAction(config, context);
				break;
			default:
				throw new Error(`Unsupported node type: ${type}`);
		}

		// Find and execute connected nodes
		const connections = Array.from(globalActionState.connections.values())
			.filter(conn => conn.sourceId === node.id);

		for (const connection of connections) {
			const nextNode = globalActionState.nodes.get(connection.targetId);
			if (nextNode) {
				await executeNode(nextNode, { ...context, previousResult: result });
			}
		}

		return result;
	};

	// Action execution implementations
	const executeNavigateAction = async (config, context) => {
		const { route, params, query } = config;
		const resolvedRoute = resolveTemplate(route, context);
		const resolvedParams = resolveTemplateObject(params || {}, context);
		const resolvedQuery = resolveTemplateObject(query || {}, context);

		// How can we use router here? Action engine starts at plugin install - we can't use useRouter() here.
		/*const router = useRouter();
		await router.push({
			path: resolvedRoute,
			params: resolvedParams,
			query: resolvedQuery
		});
		return { navigated: true, route: resolvedRoute };
		*/

		return { navigated: false };
	};

	const executeHttpAction = async (config, context) => {
		const { method = 'GET', url, data, headers } = config;
		const resolvedUrl = resolveTemplate(url, context);
		const resolvedData = resolveTemplateObject(data || {}, context);
		const resolvedHeaders = resolveTemplateObject(headers || {}, context);

		// How can we inject here safely? Action engine starts at plugin install - we can't use inject() here.
		/*
		const $http = inject('$http');
		const response = await $http.request({
			method,
			url: resolvedUrl,
			data: resolvedData,
			headers: resolvedHeaders
		});
		*/
		const response = { data: { stub: 'abc' }};

		return response.data;
	};

	const executeDialogAction = async (config, context) => {
		// This would integrate with your modal system
		const { component, props, title } = config;
		const resolvedProps = resolveTemplateObject(props || {}, context);

		// Example implementation - you'd adapt this to your modal system
		return new Promise((resolve) => {
			// Your modal implementation here
			console.log('Show dialog:', { component, props: resolvedProps, title });
			resolve({ dialogShown: true });
		});
	};

	const executeUpdateDataAction = async (config, context) => {
		const { target, value, operation = 'set' } = config;
		const resolvedValue = resolveTemplate(value, context);

		// This would update data in your components/store
		return { updated: true, target, value: resolvedValue, operation };
	};

	const executeSetVariableAction = (config, context) => {
		const { name, value } = config;
		const resolvedValue = resolveTemplate(value, context);
		globalActionState.variables.set(name, resolvedValue);
		return { variableSet: true, name, value: resolvedValue };
	};

	const executeEmitEventAction = (config, context) => {
		const { eventName, data } = config;
		const resolvedData = resolveTemplateObject(data || {}, context);

		// Emit custom event that components can listen to
		const event = new CustomEvent(eventName, { detail: resolvedData });
		document.dispatchEvent(event);

		return { eventEmitted: true, eventName, data: resolvedData };
	};

	const executeConditionalAction = async (config, context) => {
		const { condition, trueAction, falseAction } = config;
		const conditionResult = evaluateCondition(condition, context);

		if (conditionResult && trueAction) {
			return await executeNode(trueAction, context);
		} else if (!conditionResult && falseAction) {
			return await executeNode(falseAction, context);
		}

		return { conditionResult, executed: !!conditionResult };
	};

	const executeCustomFunctionAction = async (config, context) => {
		const { functionName, args } = config;

		// This would call registered custom functions
		// You could maintain a registry of custom functions
		throw new Error(`Custom function not implemented: ${functionName}`);
	};

	// Utility functions
	const resolveTemplate = (template, context) => {
		if (typeof template !== 'string') return template;

		return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
			return getNestedValue(context, path.trim()) || match;
		});
	};

	const resolveTemplateObject = (obj, context) => {
		if (!obj || typeof obj !== 'object') return obj;

		const resolved = {};
		for (const [key, value] of Object.entries(obj)) {
			resolved[key] = resolveTemplate(value, context);
		}
		return resolved;
	};

	const getNestedValue = (obj, path) => {
		return path.split('.').reduce((current, key) => {
			return current?.[key];
		}, obj);
	};

	const evaluateCondition = (condition, context) => {
		// Simple condition evaluation - you might want to use a proper expression parser
		if (typeof condition === 'string') {
			return resolveTemplate(condition, context) === 'true';
		}
		return !!condition;
	};

	// Computed properties
	const registeredNodes = computed(() => Array.from(globalActionState.nodes.values()));
	const nodeConnections = computed(() => Array.from(globalActionState.connections.values()));
	const actionVariables = computed(() => Object.fromEntries(globalActionState.variables));

	return {
		// State
		isExecuting,
		lastResult,
		errors,
		registeredNodes,
		nodeConnections,
		actionVariables,

		// Methods
		registerNode,
		createConnection,
		executeGraph,
		executeNode,

		// Utilities
		resolveTemplate,
		evaluateCondition,

		// Debug
		getEventHistory: () => globalActionState.eventHistory,
		clearHistory: () => { globalActionState.eventHistory.length = 0; },
		toggleDebugMode: () => { globalActionState.debugMode = !globalActionState.debugMode; }
	};
}